import Logo from '../assets/images/common/importerr-no-image-logo.png';

// AWS S3/CloudFront URL patterns for Importerr
const AWS_PATTERNS = {
  S3_BUCKET: 'importerr-pimages.s3.ap-south-1.amazonaws.com',
  CLOUDFRONT: 'd1wdkckkuu1tlk.cloudfront.net',
};

/**
 * Check if URL is an AWS (S3 or CloudFront) URL
 * @param {string} url - URL to check
 * @returns {boolean} - True if AWS URL
 */
export const isAwsUrl = url => {
  if (!url) return false;
  return (
    url.includes(AWS_PATTERNS.S3_BUCKET) ||
    url.includes(AWS_PATTERNS.CLOUDFRONT) ||
    url.includes('amazonaws.com') ||
    url.includes('cloudfront.net') ||
    url.includes('s3.ap-south-1')
  );
};

/**
 * Check if URL is an Alibaba CDN URL
 * @param {string} url - URL to check
 * @returns {boolean} - True if Alibaba URL
 */
export const isAlibabaUrl = url => {
  if (!url) return false;
  return url.includes('alicdn.com') || url.includes('alibaba.com') || url.includes('1688.com');
};

/**
 * Enhanced URL normalization for production environments
 * Handles Alibaba CDN URLs, AWS S3/CloudFront URLs, and other production-specific cases
 */
export const normalizeUrl = url => {
  if (!url) return '';

  try {
    let normalizedUrl = String(url).trim();

    // Skip localhost URLs (these are invalid image URLs)
    if (normalizedUrl.includes('localhost') || normalizedUrl.includes('127.0.0.1')) {
      return '';
    }

    // Skip URLs that look like app routes (not images)
    if (normalizedUrl.includes('/collection') || normalizedUrl.includes('/product-detail')) {
      return '';
    }

    // Remove any whitespace and invalid characters
    normalizedUrl = normalizedUrl.replace(/\s+/g, '');

    // Convert HTTP to HTTPS
    normalizedUrl = normalizedUrl.replace(/^http:\/\//, 'https://');

    // Handle protocol-relative URLs
    if (normalizedUrl.startsWith('//')) {
      normalizedUrl = 'https:' + normalizedUrl;
    }

    // Handle AWS S3 URLs (importerr-pimages bucket)
    if (normalizedUrl.includes(AWS_PATTERNS.S3_BUCKET) || normalizedUrl.includes('s3.ap-south-1')) {
      // Ensure HTTPS, don't modify the URL structure
      normalizedUrl = normalizedUrl.replace(/^http:\/\//, 'https://');
      // S3 URLs don't need cache busting, but ensure proper format
      // Remove any double slashes (except after https:)
      normalizedUrl = normalizedUrl.replace(/([^:])\/\//g, '$1/');
      // console.log('[imageUtils] S3 URL normalized:', normalizedUrl);
      return normalizedUrl;
    }

    // Handle CloudFront URLs (Importerr CDN)
    if (normalizedUrl.includes(AWS_PATTERNS.CLOUDFRONT) || normalizedUrl.includes('cloudfront.net')) {
      // Ensure HTTPS, don't add cache busting parameters
      normalizedUrl = normalizedUrl.replace(/^http:\/\//, 'https://');
      // Remove any double slashes (except after https:)
      normalizedUrl = normalizedUrl.replace(/([^:])\/\//g, '$1/');
      // console.log('[imageUtils] CloudFront URL normalized:', normalizedUrl);
      return normalizedUrl;
    }

    // Handle other AWS URLs (generic S3, etc.)
    if (normalizedUrl.includes('amazonaws.com') || normalizedUrl.includes('s3.')) {
      normalizedUrl = normalizedUrl.replace(/^http:\/\//, 'https://');
      // Remove any double slashes (except after https:)
      normalizedUrl = normalizedUrl.replace(/([^:])\/\//g, '$1/');
      // console.log('[imageUtils] AWS URL normalized:', normalizedUrl);
      return normalizedUrl;
    }

    // Handle Alibaba image URLs specifically
    if (isAlibabaUrl(normalizedUrl)) {
      // Ensure proper HTTPS for Alibaba CDN
      normalizedUrl = normalizedUrl.replace(/^https?:\/\//, 'https://');

      // Remove any existing cache busting parameters
      normalizedUrl = normalizedUrl.split('?')[0];

      // Add cache busting for Alibaba CDN
      normalizedUrl += `?t=${Date.now()}&v=1`;
    }

    // Ensure URL is properly encoded
    try {
      new URL(normalizedUrl);
    } catch {
      // If URL is invalid, try to encode it
      normalizedUrl = encodeURI(normalizedUrl);
    }

    return normalizedUrl;
  } catch (error) {
    console.error('[imageUtils] normalizeUrl error:', error, 'for URL:', url);
    return url;
  }
};

/**
 * Enhanced image error handler with retry logic and production debugging
 */
export const handleImgError = (e, retryCount = 0) => {
  const maxRetries = 3;
  const originalSrc = e.target.src;

  // Log for debugging
  console.log(`[imageUtils] Image error for: ${originalSrc}, retry: ${retryCount}`);

  if (retryCount < maxRetries) {
    // Try to reload the image with different cache busting strategies
    let newSrc;

    if (isAlibabaUrl(originalSrc)) {
      // For Alibaba URLs, try different cache busting approaches
      const baseUrl = originalSrc.split('?')[0];
      const timestamp = Date.now();
      const randomParam = Math.random().toString(36).substring(7);

      newSrc = `${baseUrl}?t=${timestamp}&v=${retryCount + 1}&r=${randomParam}`;
    } else if (isAwsUrl(originalSrc)) {
      // For AWS URLs (S3/CloudFront), just retry with the same URL
      // These are our own hosted images, so temporary failures should resolve
      // Don't add cache busting as it might break signed URLs
      newSrc = originalSrc;
      console.log(`[imageUtils] AWS URL retry ${retryCount + 1}: ${newSrc}`);
    } else {
      // For other URLs, use standard cache busting
      const separator = originalSrc.includes('?') ? '&' : '?';
      newSrc = `${originalSrc}${separator}retry=${retryCount + 1}&t=${Date.now()}`;
    }

    // Add a small delay before retry
    setTimeout(
      () => {
        e.target.src = newSrc;
        e.target.onerror = () => handleImgError(e, retryCount + 1);
      },
      500 * (retryCount + 1)
    ); // Increasing delay for each retry
  } else {
    // Fallback to placeholder image
    console.log(`[imageUtils] Max retries reached, using fallback for: ${originalSrc}`);
    e.target.src = Logo;
    e.target.onerror = null; // Prevent infinite loops
    e.target.style.border = '1px solid #e5e7eb';
    e.target.style.backgroundColor = '#f9fafb';
  }
};

/**
 * Production-ready image component with enhanced error handling
 */
export const createImageElement = (src, alt = '', className = '', style = {}) => {
  const img = document.createElement('img');
  img.src = normalizeUrl(src);
  img.alt = alt;
  img.className = className;
  
  // Set referrerPolicy for all images to handle CDN restrictions
  img.referrerPolicy = 'no-referrer';
  
  // Don't set crossOrigin for Alibaba or AWS URLs - it can cause CORS issues
  // Only set for other URLs if needed
  
  img.style.objectFit = 'cover';
  img.style.objectPosition = 'center';

  // Apply custom styles
  Object.assign(img.style, style);

  // Add error handling
  img.onerror = e => handleImgError(e, 0);

  return img;
};

/**
 * Preload image for better user experience
 */
export const preloadImage = url => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.referrerPolicy = 'no-referrer';

    img.onload = () => {
      resolve(url);
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${url}`));
    };

    img.src = normalizeUrl(url);
  });
};

/**
 * Check if image URL is valid and accessible
 */
export const isValidImageUrl = url => {
  if (!url) return false;

  try {
    const normalizedUrl = normalizeUrl(url);
    
    // Check if it's a valid HTTPS URL
    if (!normalizedUrl.startsWith('https://')) return false;
    
    // Check for known valid image sources
    const isKnownSource = 
      isAwsUrl(normalizedUrl) ||
      isAlibabaUrl(normalizedUrl);
    
    // Check for common image extensions
    const hasImageExtension = 
      normalizedUrl.includes('.jpg') ||
      normalizedUrl.includes('.jpeg') ||
      normalizedUrl.includes('.png') ||
      normalizedUrl.includes('.webp') ||
      normalizedUrl.includes('.gif');
    
    return isKnownSource || hasImageExtension;
  } catch {
    return false;
  }
};

/**
 * Test image URL accessibility in production
 */
export const testImageUrl = async url => {
  try {
    const response = await fetch(normalizeUrl(url), {
      method: 'HEAD',
      mode: 'no-cors', // This allows checking without CORS issues
    });
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Append size token before image extension (e.g., image.200x200.jpg)
 * Supports Alibaba CDN URLs (alicdn.com, alibaba.com) and standard image URLs
 * AWS S3/CloudFront URLs are returned as-is (they don't support this pattern)
 * @param {string} url - The image URL to transform
 * @param {string} size - The size token to append (default: '200x200')
 * @returns {string} - The transformed URL with size token
 */
export const appendSizeToImageUrl = (url, size = '200x200') => {
  if (!url || typeof url !== 'string') return url;

  // Skip size appending for AWS URLs (S3/CloudFront) - they don't support this pattern
  if (isAwsUrl(url)) {
    return url;
  }

  // Handle URLs with query parameters
  const urlParts = url.split('?');
  const baseUrl = urlParts[0];
  const queryString = urlParts.length > 1 ? `?${urlParts.slice(1).join('?')}` : '';

  // Avoid double-appending the same size token
  const alreadySized = new RegExp(`\\.${size}\\.(?:jpg|jpeg|png|webp)`, 'i');
  if (alreadySized.test(baseUrl)) return url;

  // Insert .size before final extension when ext is jpg/jpeg/png/webp
  const sizedUrl = baseUrl.replace(/\.(jpg|jpeg|png|webp)$/i, (_, ext) => `.${size}.${ext}`);

  // If the replacement didn't work (no extension found), return original URL
  if (sizedUrl === baseUrl) {
    return url;
  }

  return sizedUrl + queryString;
};
